import { ForbiddenError } from '@casl/ability';
import { useCallback, useMemo } from 'react';

import { createAppAbility, hasPortalPermissions } from '../utils/route-guards/ability.ts';
import type { ActionsOf, Entity } from '../utils/route-guards/types.ts';
import { useAuthContext } from './useAuthContext.ts';

type CanFn = <E extends Entity, A extends ActionsOf<E>>(entity: E, action: A) => boolean;
type AssertCanFn = <E extends Entity, A extends ActionsOf<E>>(entity: E, action: A) => void;

/** Shares CASL checks between navigation, route guards, forms, and API hooks. */
export const useCan = () => {
  const { authData } = useAuthContext();
  const ability = useMemo(() => createAppAbility(authData), [authData]);

  const can = useCallback<CanFn>((entity, action) => ability.can(action, entity), [ability]);
  const assertCan = useCallback<AssertCanFn>(
    (entity, action) => {
      ForbiddenError.from(ability)
        .setMessage('You do not have permission to perform this action.')
        .throwUnlessCan(action, entity);
    },
    [ability]
  );

  return { can, assertCan, hasPermissions: hasPortalPermissions(authData) };
};
